import { AbstractStep } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import {
	faMoneyBill1 as fasMoneyBill1,
	faCalendar as fasCalendar,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import { Button, FormCheck, FormCurrency, FormNumber } from "bm-ui-kit";
import dayjs from "dayjs";
import type { FormApi } from "final-form";
import React from "react";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import {
	MAX_AGE,
	MAX_BABY_LOAN_AMOUNT,
	MAX_MORTGAGE_AMOUNT,
	MAX_PERSONAL_LOAN_AMOUNT,
	MIN_BABY_LOAN_AMOUNT,
	MIN_MORTGAGE_AMOUNT,
	MIN_PERSONAL_LOAN_AMOUNT,
} from "../data";
import type { FormFields } from "../types";

export interface StepFields {
	hasBabyLoan: boolean;
	relatedBabyLoanAmount: number;
	hasMortgage: boolean;
	relatedMortgageLoanAmount: number;
	hasPersonalLoan: boolean;
	relatedPersonalLoanAmount: number;
	birthYear: number;
}

export class Step2 extends AbstractStep<StepFields> {
	protected initValues(): StepFields {
		return {
			hasBabyLoan: false,
			relatedBabyLoanAmount: undefined,
			hasMortgage: false,
			relatedMortgageLoanAmount: undefined,
			hasPersonalLoan: false,
			relatedPersonalLoanAmount: undefined,
			birthYear: undefined,
		};
	}

	protected renderContent(values: FormFields, form: FormApi<StepFields>): ReactNode {
		return (
			<>
				<Card.Body className="px-4">
					<div className="h4 my-3">
						Válaszd ki a hiteleidet, add meg a
						<br />
						tartozás összegét!
					</div>
					<FormCheck label="Jelzáloghitel" name="hasMortgage" validate={this.validHasMortgage} />
					<FormCurrency
						name="relatedMortgageLoanAmount"
						prepend={<FontAwesomeIcon icon={fasMoneyBill1} />}
						disabled={!values.hasMortgage}
						validate={this.validMortgageLoanAmount}
						required
					/>
					<FormCheck label="Személyi kölcsön" name="hasPersonalLoan" />
					<FormCurrency
						name="relatedPersonalLoanAmount"
						prepend={<FontAwesomeIcon icon={fasMoneyBill1} />}
						disabled={!values.hasPersonalLoan}
						validate={this.validPersonalLoanAmount}
						required
					/>
					<FormCheck label="Babaváró hitel" name="hasBabyLoan" />
					<FormCurrency
						name="relatedBabyLoanAmount"
						prepend={<FontAwesomeIcon icon={fasMoneyBill1} />}
						disabled={!values.hasBabyLoan}
						validate={this.validBabyLoanAmount}
						required
					/>
					<FormNumber
						label="Melyik évben születtél?"
						name="birthYear"
						autoComplete="bday-year"
						min={dayjs().subtract(MAX_AGE, "years").year()}
						maxLength={4}
						prepend={<FontAwesomeIcon icon={fasCalendar} />}
						validate={this.validBirthYear}
						onChange={(val) => this.onYearChange(val, form)}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<Button type="submit">Tovább</Button>
				</Card.Footer>
			</>
		);
	}

	private onYearChange(year: number, form: FormApi<StepFields>) {
		const stringYear = year.toString();

		if (stringYear.length > 4) {
			form.getFieldState("birthYear").change(Number(stringYear.slice(0, 4)));
		}
	}

	private validHasMortgage(_value: boolean, allValues: FormFields): string {
		if (!allValues.hasMortgage && !allValues.hasBabyLoan && !allValues.hasPersonalLoan) {
			return "Legalább egy hitelt ki kell választanod!";
		}
	}

	private validMortgageLoanAmount(amount: number): string {
		if (isNaN(amount)) {
			return "A hitel összegének megadása kötelező!";
		}

		if (amount < MIN_MORTGAGE_AMOUNT) {
			return `A megadható legalacsonyabb tartozás ${formatText(MIN_MORTGAGE_AMOUNT, "ft")}!`;
		}

		if (amount > MAX_MORTGAGE_AMOUNT) {
			return `A megadható legmagasabb tartozás ${formatText(MAX_MORTGAGE_AMOUNT, "ft")}!`;
		}
	}

	private validPersonalLoanAmount(amount: number): string {
		if (isNaN(amount)) {
			return "A hitel összegének megadása kötelező!";
		}

		if (amount < MIN_PERSONAL_LOAN_AMOUNT) {
			return `A megadható legalacsonyabb tartozás ${formatText(MIN_PERSONAL_LOAN_AMOUNT, "ft")}!`;
		}

		if (amount > MAX_PERSONAL_LOAN_AMOUNT) {
			return `A megadható legmagasabb tartozás ${formatText(MAX_PERSONAL_LOAN_AMOUNT, "ft")}!`;
		}
	}

	private validBabyLoanAmount(amount: number): string {
		if (isNaN(amount)) {
			return "A hitel összegének megadása kötelező!";
		}

		if (amount < MIN_BABY_LOAN_AMOUNT) {
			return `A megadható legalacsonyabb tartozás ${formatText(MIN_BABY_LOAN_AMOUNT, "ft")}!`;
		}

		if (amount > MAX_BABY_LOAN_AMOUNT) {
			return `A megadható legmagasabb tartozás ${formatText(MAX_BABY_LOAN_AMOUNT, "ft")}!`;
		}
	}

	private validBirthYear(year: number): string {
		if (!year) {
			return "Kérjük adjon meg születési évet!";
		}
		const actualYear = dayjs().year();

		if (year < actualYear - MAX_AGE) {
			return `A születési éve nem lehet kisebb mint ${actualYear - MAX_AGE}`;
		}

		if (year > actualYear - 18) {
			return `A születési éve nem lehet nagyobb mint ${actualYear - 18}`;
		}
	}

	protected getComponentName(): string {
		return "Step2";
	}
}
